import {
  MarketAccessory, MarketActionCard, MarketButton, MarketList, MarketRadio, MarketRow,
} from '@market/react';
import type { MarketListCustomEvent, TMarketListSelectionsDidChangeEventDetail } from '@market/web-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MarketDogearedPaperSignDollarIcon, MarketPercentageIcon,
} from '@squareup/market-react/icons';
import { withI18n } from '@squareup/service-i18n/components';
import styles from './RepaymentMethodSelection.module.css';

export type RepaymentMethod = 'smart-repayment-and-autopay' | 'autopay';

export interface RepaymentMethodSelectionProps {
  readonly onNext: (method: RepaymentMethod) => void;
}

export const RepaymentMethodSelection = ({
  onNext,
}: RepaymentMethodSelectionProps) => {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<RepaymentMethod>(
    'smart-repayment-and-autopay',
  );

  const handleSelectionChange = (
    event: MarketListCustomEvent<TMarketListSelectionsDidChangeEventDetail>,
  ) => {
    const newValue = event.detail.currentSelectionValues?.[0];
    if (newValue) {
      setSelectedMethod(newValue as RepaymentMethod);
    }
  };

  const handleNext = () => {
    onNext(selectedMethod);
  };

  return (
    <div className={styles.content}>
      <h2 className="heading-30">{t('bankingShared.creditCard.repaymentMethodSelection.title')}</h2>
      <MarketList
        interactive
        value={selectedMethod}
        onMarketListSelectionsDidChange={handleSelectionChange}
      >
        <MarketActionCard value="smart-repayment-and-autopay">
          <MarketRow>
            <MarketAccessory
              className={styles.accessoryIcon}
              slot="leading-accessory"
            >
              <MarketPercentageIcon />
            </MarketAccessory>
            <label slot="label">{t('bankingShared.creditCard.repaymentMethodSelection.dailySales')}</label>
            <p
              className={styles.optionSubtext}
              slot="subtext"
            >
              {t('bankingShared.creditCard.repaymentMethodSelection.dailySalesSubtext')}
            </p>
            <MarketRadio slot="control" />
          </MarketRow>
        </MarketActionCard>

        <MarketActionCard value="autopay">
          <MarketRow>
            <MarketAccessory
              className={styles.accessoryIcon}
              slot="leading-accessory"
            >
              <MarketDogearedPaperSignDollarIcon />
            </MarketAccessory>
            <label slot="label">{t('bankingShared.creditCard.repaymentMethodSelection.monthly')}</label>
            <p
              className={styles.optionSubtext}
              slot="subtext"
            >
              {t('bankingShared.creditCard.repaymentMethodSelection.monthlySubtext')}
            </p>
            <MarketRadio slot="control" />
          </MarketRow>
        </MarketActionCard>
      </MarketList>

      <MarketButton
        rank="primary"
        onClick={handleNext}
      >
        {t('bankingShared.creditCard.repaymentMethodSelection.next')}
      </MarketButton>
    </div>
  );
};

export const RepaymentMethodSelectionI18n = withI18n(RepaymentMethodSelection);
